import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { authApi, getAuthToken, setAuthToken, tokenClaims, API } from "./api";

const AuthContext = createContext(null);

const ACTING_KEY = "containerflow_acting_company_id";
const ACTING_HEADER = "X-Acting-Company-Id";

function applyActingHeader(value) {
  if (value) {
    axios.defaults.headers.common[ACTING_HEADER] = value;
  } else {
    delete axios.defaults.headers.common[ACTING_HEADER];
  }
}

// Apply persisted acting-company header on first import (page reload)
try {
  const persisted = localStorage.getItem(ACTING_KEY);
  if (persisted) applyActingHeader(persisted);
} catch (_) { /* ignore */ }

// 2026-10-22: Uden forbindelse til serveren (telefon uden net, server nede)
// bruges den sidst kendte bruger for samme login, så chaufføren ikke bliver
// logget ud. Kun en rigtig afvisning (401/403) logger ud.
const LAST_USER_KEY = "containerflow_last_user";
const isOffline = (e) => !e?.response || e.response.status >= 500;
function saveLastUser(token, user) {
  try { localStorage.setItem(LAST_USER_KEY, JSON.stringify({ t: String(token || "").slice(-24), user })); } catch (_) { /* ignore */ }
}
function loadLastUser(token) {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_USER_KEY) || "null");
    return v && token && v.t === String(token).slice(-24) ? v.user : null;
  } catch (_) { return null; }
}

export function AuthProvider({ children }) {
  // user: null = unauthenticated, undefined = still checking, object = logged in
  const [user, setUser] = useState(undefined);
  const [actingCompanyId, _setActingCompanyId] = useState(() => {
    try { return localStorage.getItem(ACTING_KEY) || ""; } catch (_) { return ""; }
  });

  const setActingCompanyId = useCallback((cid) => {
    const next = cid || "";
    try {
      if (next) localStorage.setItem(ACTING_KEY, next);
      else localStorage.removeItem(ACTING_KEY);
    } catch (_) { /* ignore */ }
    applyActingHeader(next);
    _setActingCompanyId(next);
  }, []);

  // Load user on mount if token exists
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      return;
    }
    authApi
      .me()
      .then((u) => { setUser(u); saveLastUser(token, u); })
      .catch((e) => {
        const last = loadLastUser(token);
        if (isOffline(e) && last) { setUser(last); return; }
        setAuthToken(null);
        setUser(null);
      });
  }, []);

  // 2026-10-25: Efter kodeord og evt. 2FA - gem login'et.
  const completeLogin = useCallback((data) => {
    setAuthToken(data.token);
    setUser(data.user);
    saveLastUser(data.token, data.user);
    applyActingHeader("");
    _setActingCompanyId("");
    try { localStorage.removeItem(ACTING_KEY); } catch (_) { /* ignore */ }
    return data.user;
  }, []);

  const login = useCallback(async (email, password, companyId = "") => {
    const data = await authApi.login(email, password, companyId);
    // 2026-10-30: Flere firmaer med samme e-mail og kodeord - login-siden viser valget.
    if (Array.isArray(data.choose_company)) return { chooseCompany: data.choose_company };
    // 2026-10-25: 2FA-trinnet (kode eller opsætning) håndteres af login-siden.
    if (data.mfa_required || data.mfa_setup_required) return { mfaStep: data };
    setAuthToken(data.token);
    setUser(data.user);
    saveLastUser(data.token, data.user);
    // Clear any stale acting-company on fresh login
    applyActingHeader("");
    _setActingCompanyId("");
    try { localStorage.removeItem(ACTING_KEY); } catch (_) { /* ignore */ }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setAuthToken(null);
    setUser(null);
    applyActingHeader("");
    _setActingCompanyId("");
    // Clear other legacy state so the old driver setup re-appears for an unauthenticated visitor
    try {
      localStorage.removeItem("korkman2_driver_name");
      localStorage.removeItem("korkman2_vehicle_reg");
      localStorage.removeItem("korkman2_session");
      localStorage.removeItem(ACTING_KEY);
      localStorage.removeItem(LAST_USER_KEY);
    } catch (_) { /* ignore */ }
    // 2026-10-25 (sikkerhed): Offline-cachen tømmes, så den næste bruger på
    // samme enhed ikke kan se den forriges data uden net.
    try { if (window.caches) (await window.caches.keys()).filter(k => k.startsWith("containerflow-data")).forEach(k => window.caches.delete(k)); } catch (_) { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      saveLastUser(getAuthToken(), u);
      return u;
    } catch (e) {
      if (isOffline(e)) return null;  // behold den nuværende bruger uden net
      setUser(null);
      return null;
    }
  }, []);

  // Fase 15.40b (2026-02-22): Dynamisk feature-flag-opdatering. Når super-
  // admin toggler en feature, skal det slå igennem på brugerens UI uden
  // logout. Vi polller /clients/me/features hvert 30. sekund + når browser-
  // fanen får fokus igen (tab switch / vinduet kommer i fokus). Det er nok
  // til at give "near real-time"-følelse uden websocket-kompleksitet.
  const reloadFeaturesOnly = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/clients/me/features`);
      const nextFeatures = (data && data.features) || {};
      setUser((u) => {
        if (!u) return u;
        // Skip update hvis features er identiske (undgår unødvendige re-renders).
        const cur = u.features || {};
        const keys = new Set([...Object.keys(cur), ...Object.keys(nextFeatures)]);
        let changed = false;
        for (const k of keys) {
          if (Boolean(cur[k]) !== Boolean(nextFeatures[k])) { changed = true; break; }
        }
        if (!changed) return u;
        return { ...u, features: nextFeatures };
      });
    } catch (_) { /* ignore - næste poll prøver igen */ }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    // Kør én gang med det samme efter user-load, så vi får friske features
    // umiddelbart efter login (dækker over evt. ændringer mens brugeren var
    // logget ud). Derefter hver 30. sekund.
    reloadFeaturesOnly();
    const iv = setInterval(reloadFeaturesOnly, 30_000);
    // Når fanen kommer i fokus igen, refresh straks (typisk efter tab-switch).
    const onFocus = () => { reloadFeaturesOnly(); };
    const onVisibility = () => { if (document.visibilityState === "visible") reloadFeaturesOnly(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, reloadFeaturesOnly]);

  // 2026-10-25 (S2): Chaufførens login fornyes, mens appen bruges (30 dage fra
  // sidste brug) - så han ikke skal logge ind hver morgen. Højst én gang i døgnet.
  useEffect(() => {
    if (user?.role !== "driver") return undefined;
    const renew = async () => {
      const iat = tokenClaims().iat || 0;
      if (document.hidden || !iat || Date.now() / 1000 - iat < 24 * 3600) return;
      try {
        const { data } = await axios.post(`${API}/auth/renew`);
        if (data?.token) { setAuthToken(data.token); saveLastUser(data.token, user); }
      } catch (_) { /* uden net: næste gang appen bruges */ }
    };
    renew();
    const iv = setInterval(renew, 3 * 3600 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") renew(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const value = {
    user, login, completeLogin, logout, refresh, setUser,
    actingCompanyId, setActingCompanyId,
    // Fase 15.40 (2026-02-22): Central feature-flag-helper. Læser features
    // fra det cachede user-objekt (sat ved login + /auth/me + polling).
    isFeatureEnabled: (key) => Boolean(user && user.features && user.features[key]),
    features: (user && user.features) || {},
    // Eksposeret så fx super-admin's egen toggle-fane kan force-refresh sit
    // eget cached state med det samme (uden at vente på næste poll).
    reloadFeatures: reloadFeaturesOnly,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Fase 15.40 (2026-02-22): Tynd convenience-hook der bare returnerer
// isFeatureEnabled-funktionen. Brug overalt hvor du vil gate UI:
//   const isEnabled = useFeatureFlag();
//   if (!isEnabled('estimate')) return null;
export function useFeatureFlag() {
  return useAuth().isFeatureEnabled;
}
